import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { JudgingCounts, errorMessage, fetchResetPreview, resetJudging } from '../../services/manageApi';
import '../../components/Auth/RegisterModal.css';

interface Props {
  competitionId: number;
  title: string;
  onClose: () => void;
  onDone: () => void;
}

function describe(counts: JudgingCounts): string[] {
  return [
    `${counts.matches + counts.team_matches} pairwise vote(s)`,
    `${counts.manual_scores} manual score(s)`,
    `${counts.rubric_evaluations} rubric evaluation(s)`,
    `${counts.pair_reservations} pair reservation(s)`,
    `the finalists snapshot (${counts.finalists} row(s))`,
  ];
}

const ResetJudgingDialog: React.FC<Props> = ({ competitionId, title, onClose, onDone }) => {
  const [counts, setCounts] = useState<JudgingCounts | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<JudgingCounts | null>(null);

  useEffect(() => {
    fetchResetPreview(competitionId)
      .then(setCounts)
      .catch((err) => setError(errorMessage(err, "Couldn't load what would be reset")));
  }, [competitionId]);

  const matches = typed.trim() === title.trim();

  useEscapeToClose(onClose, { enabled: !busy });

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await resetJudging(competitionId, typed);
      setDone(result);
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Failed to reset judging'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="register-modal-backdrop">
      <div className="register-modal" role="dialog" aria-modal="true" aria-labelledby="reset-judging-title">
        <div className="register-modal__header">
          <h2 className="register-modal__title" id="reset-judging-title">Reset judging</h2>
        </div>
        <div className="register-modal__form">
          {done ? (
            <>
              <p>Judging was reset. Removed: {describe(done).join(', ')}. All judges can vote again from 0.</p>
              <div className="register-modal__actions">
                <Button variant="primary" onClick={onClose}>Close</Button>
              </div>
            </>
          ) : (
            <>
              {!counts && !error && <LoadingSpinner size="sm" text="Counting votes..." />}
              {counts && (
                <>
                  <p><strong>This cannot be undone.</strong> It will delete:</p>
                  <ul className="danger-dialog__list">{describe(counts).map((line) => <li key={line}>{line}</li>)}</ul>
                  <p>
                    It resets used votes to 0 for {counts.judges} judge(s), ELO ratings to 1000, final scores and
                    rankings, and pair usage. Submissions, automatic grader scores, judges&apos; allowances and the
                    rubric are kept.
                  </p>
                  <label className="manage-field__label" htmlFor="reset-confirm-title">
                    Type the competition title to confirm: {title}
                  </label>
                  <input
                    id="reset-confirm-title"
                    className="input input--default input--md"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    disabled={busy}
                    autoComplete="off"
                    autoFocus
                  />
                </>
              )}
              {error && <div className="register-modal__error" role="alert">{error}</div>}
              <div className="register-modal__actions">
                <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="danger" onClick={submit} loading={busy} disabled={!counts || !matches || busy}>
                  Reset judging
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetJudgingDialog;
